import { useCallback, useState } from 'react';

import authRequest from '../../../../utils/fetch';
import { getAgentSettingsUrl } from '../constants';
import type { AgentSettingsResponse } from '../types';
import { devAIWorkspaceContainerType, rovoDevCLISettingsKey } from '../types';

export const fetchRovoDevCLISettings = async (
	containerId: string,
): Promise<AgentSettingsResponse> => {
	const settingsRequest = {
		containerType: devAIWorkspaceContainerType,
		containerId,
		key: rovoDevCLISettingsKey,
	};

	const response = await fetch(
		authRequest(getAgentSettingsUrl, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				settings: [settingsRequest],
			}),
		}),
	);

	if (!response.ok) {
		throw new Error(`Error: ${response.statusText}`);
	}

	const data = await response.json();
	if (!data.success) {
		throw new Error('Failed to get Rovo Dev CLI settings');
	}

	return data;
};

export const useGetRovoDevCLISettings = () => {
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [rovoDevCLISettingsCache, setRovoDevCLISettingsCache] = useState<Map<string, boolean>>(
		new Map(),
	);

	const getRovoDevCLISettings = useCallback(async (containerId: string) => {
		setLoading(true);
		setError(null);

		try {
			const data = await fetchRovoDevCLISettings(containerId);
			setLoading(false);
			return data.settings[0];
		} catch (e) {
			setError('An error has occurred while getting Rovo Dev CLI settings');
			setLoading(false);
			return null;
		}
	}, []);

	return {
		getRovoDevCLISettings,
		rovoDevCLISettingsCache,
		setRovoDevCLISettingsCache,
		loading,
		error,
	};
};
